import {Component, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IConnectionStatus, IFdeMetaData, IFDEResponse, ILongPollingItem, TFDEStatus} from './interface';
import {FdeLongPollingService} from './fde-long-polling.service';
import {Subscription} from 'rxjs';
import {BankAccountService} from '@services/bank-account.service';
import {finalize} from 'rxjs/operators';
import {BsModalService} from 'ngx-bootstrap/modal';
import {NotificationService} from '@notifications';
import {MfaService} from '../mfa/mfa.service';
import {IMFAItemResult} from '../mfa/interface';
import {useSpinner} from '@spinner';
import {StatsComponent} from './stats/stats.component';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';

/**
 * Component to observe fde-like bank connections. Until someday websockets solve this issue
 *  (don't ask me why, I'm asking for 2 years for that)
 *
 * Primary use case
 * 1. Someone selects fde bank. Depending on it's fde status, it should either show credentials/captcha/sq form either wait for manual update.
 * 2. If update has been triggered by the user, this component should start the observation of that bank:
 *  2.1 Put bank in a queue
 *  2.2 If queue is empty, force bank information read
 *  2.3 Trigger a modal if required, pause everything until modal processed.
 */
@Component({
  selector: 'app-fde-long-polling',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule],
  template: '',
})
export class FdeLongPollingComponent implements OnDestroy {
  // queue to process connections, saved in localstorage
  connectionsQueue: number[] = [];
  // indexed connections, by connection id
  connectionsMap: { [key: number]: IConnectionStatus } = {};
  // currently processing connections
  processingBuffer: number[] = [];
  // queue tick interval
  queueInterval: ReturnType<typeof setInterval>;
  // status retrieval is in progress. to prevent concurrent requests with unpredictable resolving
  statusIsUpdating: boolean;

  subscriptions: Subscription[] = [];

  constructor(
    private fdeLongPollingService: FdeLongPollingService,
    private modalService: BsModalService,
    private matSnackBar: MatSnackBar,
    private notificationService: NotificationService,
    private mfaService: MfaService,
    private bankAccountService: BankAccountService,
  ) {
    this.loadConnections();
    this.initSubscription();
    this.startQueueProcess();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Retrieve processing connections list from localstorage
   */
  loadConnections() {
    this.connectionsQueue = this.fdeLongPollingService.loadState();
  }

  /**
   * Initialize subscription to observe selections, refreshes, etc
   */
  initSubscription() {
    this.subscriptions.push(
      this.fdeLongPollingService.connectionEngaged.subscribe((connectionId: number) => {
        // another connection has been selected
        if (this.connectionsQueue.includes(connectionId)) {
          // we already have such connection, don't do anything
          return;
        }

        // push connection to queue
        this.pushToQueue(connectionId, true);
      })
    );
    this.subscriptions.push(
      this.mfaService.processed.subscribe((data) => this.connectionMFACallback(data))
    );
  }

  async connectionMFACallback(data: IMFAItemResult) {
    const result = await this.processMFA(data);
    if (result) {
      // only if callback has successfully been processed
      this.pushToQueue(data.connectionId);
    }
    this.clearConnection(data.connectionId);
    this.startQueueProcess();
  }

  /**
   * Initialize queue processing
   */
  startQueueProcess() {
    this.queueInterval = setInterval(() => {
      if (!this.connectionsQueue.length || this.statusIsUpdating) {
        // something in currently processing or no connections at all
        return;
      }
      // always processing first element in queue
      this.processConnection(this.connectionsQueue.shift());
    }, 5000);
  }

  /**
   * Idle queue process in case of MFA
   */
  stopQueueProcess(connectionIdToStop: number) {
    // stopping queue process
    clearInterval(this.queueInterval);

    // connections are being processed simultaneously with connection which initiated stop
    const restConnections = this.processingBuffer.filter(
      (item) => item !== connectionIdToStop
    );
    // currently only one stopped connection will be in process
    this.processingBuffer = [connectionIdToStop];
    // other connections will be returned to queue, due to possible change of their state
    this.connectionsQueue = [...restConnections, ...this.connectionsQueue];
    // state has been changed
    this.fdeLongPollingService.saveState(this.connectionsQueue);
  }

  /**
   * Process connection
   * @param connectionId
   */
  processConnection(connectionId: number) {
    if (this.processingBuffer.includes(connectionId)) {
      // current connection already being processed
      return;
    }

    this.processingBuffer.push(connectionId);
    this.statusIsUpdating = true;
    // this.fdeLongPollingService.updateState({
    //   connectionId, message: 'Retrieving information from service...', status: 'UPDATING'
    // });
    this.fdeLongPollingService.getConnectionStatus(connectionId)
    .pipe(finalize(() => this.statusIsUpdating = false))
    .subscribe({
      next: (status) => {
        // saving result of status read
        this.connectionsMap[connectionId] = status;
        // process status
        this.processStatus(connectionId, status);
      },
      error: () => this.fdeLongPollingService.saveState(this.connectionsQueue),
    });
  }

  /**
   * Will process connection according to its fde status
   * @param connectionId
   * @param status
   */
  processStatus(connectionId: number, status: IConnectionStatus) {
    const statusType: TFDEStatus = status?.fdeAccountResponse?.statusType;
    if (statusType === 'FORM_REQUESTED') {
      // O, F, E - MFA queue,
      //  connection id will be stored in processing buffer until mfa is resolved,
      //  connection processing will also be stopped
      this.stopQueueProcess(connectionId);
      this.fdeLongPollingService.updateState({
        connectionId, message: 'Waiting for an answer to the question',
        status: 'WARNING',
      });
      return this.showMFA({connectionId, status});
    }

    // no matter what status, just an updated info from fde
    this.fdeLongPollingService.updateState({
      connectionId,
      message: status.fdeAccountResponse.statusMessage,
      status: statusType === 'EXECUTED' ? 'UPDATING' :
        statusType === 'UPDATED_SUCCESSFULLY' ? 'UPDATED' :
          statusType === 'UPDATED_ERROR' ? 'FAILED' : 'WARNING',
    });

    if (!statusType || ([
      'UPDATED_SUCCESSFULLY', 'UPDATE_CANCELLED', 'UPDATED_ERROR',
    ] as TFDEStatus[]).includes(statusType)) {
      // no status or M, R, W - done
      // saving current queue state
      this.fdeLongPollingService.saveState(this.connectionsQueue);
      this.fdeLongPollingService.finish({
        connectionId, statusType: statusType || null, message: status.fdeAccountResponse?.statusMessage,
      });

      if (statusType === 'UPDATED_SUCCESSFULLY' && status.fdeAccountResponse.metadata?.type === 'STATS') {
        // want to show a popup with summary info about extractor
        this.showExtractorStats({clientName: status.clientName, ...status.fdeAccountResponse});
      }

      return this.clearConnection(connectionId);
    }

    if (statusType === 'EXECUTED') {
      // X and A - continue polling, back to queue
      this.pushToQueue(connectionId);
      // but clear from processing buffer and remove data about status, because it is unnecessary
      this.clearConnection(connectionId);
    }
  }

  /**
   * Clear connection from long polling
   * @param connectionId
   */
  clearConnection(connectionId: number) {
    this.removeFromProcessing(connectionId);
    delete this.connectionsMap[connectionId];
  }

  /**
   * Removes connection from buffer
   * @param connectionId
   */
  removeFromProcessing(connectionId: number) {
    this.processingBuffer = this.processingBuffer.filter((item) => item !== connectionId);
  }

  /**
   * Push connection to queue with saving it to localstorage
   * @param connectionId
   * @param updateState send status update, to notify user on first add to queue
   */
  pushToQueue(connectionId: number, updateState?: boolean) {
    if (updateState) {
      // has to be only for the first time, when connection is engaged, or there will be a status loss
      this.fdeLongPollingService.updateState({
        connectionId,
        status: 'UPDATING',
        message: 'Waiting in queue..',
      });
    }
    this.connectionsQueue.push(connectionId);
    this.fdeLongPollingService.saveState(this.connectionsQueue);
  }

  /**
   * Showing MFA modal with question(s)
   * @param data
   */
  showMFA(data: ILongPollingItem) {
    const {connectionId, status} = data;
    if (status?.fdeAccountResponse?.metadata?.type !== 'FORM') {
      // FORM_REQUESTED but no form provided
      console.error('FDE RETURNED', status);
      // will not retry the connection because of error from fde
      this.clearConnection(connectionId);
      // restart queue
      this.startQueueProcess();
      return this.notificationService.error('Failed to show MFA form. Please reload the page and try again.');
    }

    this.mfaService.request({
      form: status.fdeAccountResponse,
      connectionId,
      clientName: status.clientName,
      clientUsername: status.clientUsername
    });
  }

  /**
   * Will process mfa modal answer
   * And decide whether it necessary to keep connection in polling state
   */
  processMFA(data: IMFAItemResult): Promise<boolean> {
    return new Promise((resolve) => {
      // todo rewrite to one pipe subscribe processing, when catchError will be removed from cancelFDERefresh
      if (data.result) {
        const response = this.prepareForm(
          data.form, this.connectionsMap[data.connectionId].clientUsername);
        this.fdeLongPollingService.updateState({
          connectionId: data.connectionId,
          status: 'UPDATING',
          message: 'Processing response..'
        });
        this.bankAccountService.sendResponse(response, data.connectionId)
        .pipe(
          useSpinner(),
          // will be put to the queue in any case
          finalize(() => resolve(true))
        )
        .subscribe({
          // and will show error just in case
          error: (error) => this.notificationService.error(error)
        });
      } else {
        this.fdeLongPollingService.updateState({
          connectionId: data.connectionId,
          status: 'UPDATING',
          message: 'Stopping extractor due to user request..',
        });
        this.bankAccountService.cancelFDERefresh(data.connectionId)
        .pipe(useSpinner())
        .subscribe({
          next: () => resolve(true),
          error: (error) => {
            // if user refused to answer the question, no need to push it into the queue because it might become a loop
            resolve(false);
            this.fdeLongPollingService.updateState({
              connectionId: data.connectionId,
              status: 'FAILED',
              message: 'Account update suspended due to user request',
            });
            this.fdeLongPollingService.finish({
              connectionId: data.connectionId,
              statusType: 'FAILED',
              message: 'Account update suspended due to user request',
            });
            this.notificationService.error(error);
          },
        });
      }
    });
  }

  prepareForm(formData: IFDEResponse, username: string) {
    const {formFields} = formData.metadata.form;
    formFields.forEach((item) => {
      if (item.fieldType === 'LIST' && !!item.answer) {
        const value = item.formListOptions.find((option) => option.key.toString() === item.answer.toString());
        item.value = (value || {}).value;
      }
    });
    return {
      fdeAnswer: {
        accountCode: formData.accountCode,
        // legacy?
        // customerCode: formData.customerCode,
        // extractorCode: formData.extractorCode,
        form: {
          formFields
        },
        // legacy?
        // userId: formData.userId,
        username
      }
    };
  }

  showExtractorStats(data: IFDEResponse) {
    this.matSnackBar.openFromComponent(StatsComponent, {
      duration: 0,
      data,
      horizontalPosition: 'end',
    });
  }
}
